import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchKeyframes,
  fetchObservations,
  createObservation,
  patchObservation,
  patchObservedFeature,
  patchKeyframe,
  deleteObservation,
} from "../services/inspectionApi";
import {
  showSuccessNotification,
  showErrorNotification,
} from "../utils/notifications";
import AppError from "../utils/AppError";

/** Keeps data on screen across refetches, but never across an org switch. */
const keepPreviousWithinOrg = (orgScope) => (previousData, previousQuery) =>
  previousQuery?.queryKey?.at(-1) === orgScope ? previousData : undefined;

/**
 * Custom hook to fetch keyframes for a footage
 * @param {string} footageUuid - The UUID of the footage (from inspection.footage.id)
 * @param {string} orgScope - The current org scope, so a switch refetches rather than serving another org's frames
 * @returns {Object} TanStack Query result object with data, isLoading, error, etc.
 */
export const useKeyframesQuery = (footageUuid, orgScope) => {
  return useQuery({
    queryKey: ["keyframes", footageUuid, orgScope],
    queryFn: () => fetchKeyframes(footageUuid),
    enabled: !!footageUuid,
    staleTime: 5 * 60 * 1000, // 5 minutes - keyframes can be updated during review
    placeholderData: keepPreviousWithinOrg(orgScope),
  });
};

/**
 * Custom hook to fetch observations for an inspection
 * @param {string} inspectionUuid - The UUID of the inspection
 * @param {string} orgScope - The current org scope, so a switch refetches rather than serving another org's observations
 * @returns {Object} TanStack Query result object with data, isLoading, error, etc.
 */
export const useObservationsQuery = (inspectionUuid, orgScope) => {
  return useQuery({
    queryKey: ["observations", inspectionUuid, orgScope],
    queryFn: () => fetchObservations(inspectionUuid),
    enabled: !!inspectionUuid,
    staleTime: 5 * 60 * 1000, // 5 minutes - observations can be updated during review
    placeholderData: keepPreviousWithinOrg(orgScope),
  });
};

/**
 * Custom hook to create an observation for an inspection
 * @returns {Object} TanStack Mutation object with mutate, mutateAsync, isLoading, error, etc.
 */
export const useCreateObservationMutation = () => {
  return useMutation({
    mutationFn: ({ inspectionUuid, payload }) =>
      createObservation(inspectionUuid, payload),
    onSuccess: (data, variables) => {
      if (!variables.silent) {
        showSuccessNotification("Success", "Observation created successfully!");
      }
    },
    onError: (error) => {
      console.error("Error creating observation:", error);
      const appError = new AppError({
        title: "Failed to Create Observation",
        response: error.response,
        userMessage: "Could not create the observation.",
      });
      showErrorNotification(appError);
    },
  });
};

/**
 * Custom hook to patch a single observation
 * @returns {Object} TanStack Mutation object with mutate, mutateAsync, isLoading, error, etc.
 */
export const usePatchObservationMutation = () => {
  return useMutation({
    mutationFn: ({ observationId, payload }) =>
      patchObservation(observationId, payload),
    onSuccess: (data, variables) => {
      if (!variables.silent) {
        showSuccessNotification("Success", "Observation updated successfully!");
      }
    },
    onError: (error) => {
      console.error("Error updating observation:", error);
      const appError = new AppError({
        title: "Failed to Update Observation",
        response: error.response,
        userMessage: "Could not update the observation.",
      });
      showErrorNotification(appError);
    },
  });
};

/**
 * Custom hook to patch an observed feature
 * @returns {Object} TanStack Mutation object with mutate, mutateAsync, isLoading, error, etc.
 */
export const usePatchObservedFeatureMutation = () => {
  return useMutation({
    mutationFn: ({ observedFeatureId, payload }) =>
      patchObservedFeature(observedFeatureId, payload),
    onSuccess: (data, variables) => {
      if (!variables.silent) {
        showSuccessNotification("Success", "Feature updated successfully!");
      }
    },
    onError: (error) => {
      console.error("Error updating feature:", error);
      const appError = new AppError({
        title: "Failed to Update Feature",
        response: error.response,
        userMessage: "Could not update the feature.",
      });
      showErrorNotification(appError);
    },
  });
};

/**
 * Custom hook to patch a keyframe
 * @returns {Object} TanStack Mutation object with mutate, mutateAsync, isLoading, error, etc.
 */
export const usePatchKeyframeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ keyframeId, payload }) => patchKeyframe(keyframeId, payload),
    onMutate: async ({ keyframeId, payload, footageUuid }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      if (footageUuid) {
        await queryClient.cancelQueries({
          queryKey: ["keyframes", footageUuid],
        });
      }

      // Snapshot every org-scoped variant of this footage's keyframe cache
      const previousKeyframes = footageUuid
        ? queryClient.getQueriesData({ queryKey: ["keyframes", footageUuid] })
        : [];

      // Optimistically update the keyframe in the cache
      if (footageUuid) {
        queryClient.setQueriesData(
          { queryKey: ["keyframes", footageUuid] },
          (old) => {
            if (!Array.isArray(old)) return old;
            return old.map((keyframe) =>
              keyframe.id === keyframeId ? { ...keyframe, ...payload } : keyframe
            );
          }
        );
      }

      // Return context object with the snapshotted value
      return { previousKeyframes, footageUuid };
    },
    onError: (error, variables, context) => {
      // If the mutation fails, roll back to the previous value
      context?.previousKeyframes?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });

      console.error("Error updating keyframe:", error);
      const appError = new AppError({
        title: "Failed to Update Keyframe",
        response: error.response,
        userMessage: "Could not update the keyframe.",
      });
      showErrorNotification(appError);
    },
    onSuccess: (data, variables) => {
      if (!variables.silent) {
        showSuccessNotification("Success", "Keyframe updated successfully!");
      }
    },
  });
};

/**
 * Custom hook to delete an observation
 * @returns {Object} TanStack Mutation object with mutate, mutateAsync, isLoading, error, etc.
 */
export const useDeleteObservationMutation = () => {
  return useMutation({
    mutationFn: ({ observationId }) => deleteObservation(observationId),
    onSuccess: (data, variables) => {
      if (!variables.silent) {
        showSuccessNotification("Success", "Observation deleted successfully!");
      }
    },
    onError: (error) => {
      console.error("Error deleting observation:", error);
      const appError = new AppError({
        title: "Failed to Delete Observation",
        response: error.response,
        userMessage: "Could not delete the observation.",
      });
      showErrorNotification(appError);
    },
  });
};
